const initCollectionNavigationReveal = () => {
  if (!('IntersectionObserver' in window)) {
    return;
  }

  const cards = document.querySelectorAll('.cnav-card[reveal-on-scroll="true"]');
  if (!cards.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15 }
  );

  cards.forEach((card) => {
    observer.observe(card);
  });
};

const setActiveCard = (cards, activeCard) => {
  cards.forEach((card) => {
    card.classList.toggle('is-active', card === activeCard);
  });
};

const applyProductFilter = (handle) => {
  const products = document.querySelectorAll('.cnav-filterable-product');
  const seenProductHandles = new Set();

  products.forEach((product) => {
    product.classList.remove('cnav-striped-duplicate');

    // Reset any visual reordering from a previous "all categories" pass — see the
    // .cnav-striped-duplicate handling below. Must never leave this lingering once a specific
    // category is selected (or once a product stops being a duplicate), or it'll keep displacing
    // that product's real, in-place position within its own collection's product grid — which is
    // exactly what corrupts the inline banner's "after the 3rd/7th product" placement for whichever
    // category the moved product actually belongs to.
    const card = product.querySelector('.pcard');
    if (card) card.style.order = '';

    // Inline banners (see onuia_collection-single-filter.liquid) share this same wrapper/data
    // attribute as products, but should only ever show while one specific category is filtered
    // in — never in the unfiltered "all categories" view.
    const isBanner = product.querySelector('.collection-inline-banner') !== null;

    if (!handle) {
      if (isBanner) {
        product.hidden = true;
        return;
      }

      // A product that belongs to more than one combined collection renders once per collection
      // (see onuia_collection-single-filter.liquid) so per-category filtering still works — but
      // with no category selected, only the first copy of each product should actually show.
      const productHandle = product.querySelector('.pcard')?.getAttribute('data-handle');
      const isDuplicate = Boolean(productHandle) && seenProductHandles.has(productHandle);
      if (productHandle) seenProductHandles.add(productHandle);

      const categories = (product.getAttribute('data-categories') || '').trim().split(/\s+/);
      const isStripedCopy = isDuplicate && categories.includes('sale-stripe');

      if (isStripedCopy) {
        // Still the same product, but the striped version is worth browsing on its own — keep it
        // visible instead of hiding it as a duplicate, moved after every other product visually
        // (CSS order, not DOM position) so its own collection's DOM order — and thus its inline
        // banner's position — is never disturbed.
        product.hidden = false;
        product.classList.add('cnav-striped-duplicate');
        if (card) card.style.order = '9999';
      } else {
        product.hidden = isDuplicate;
      }
      return;
    }

    const categories = (product.getAttribute('data-categories') || '').trim().split(/\s+/);
    product.hidden = !categories.includes(handle);
  });

  // Let onuia_collection-single-filter.liquid's own script know the visible product
  // set changed, so it can hide color swatches that aren't offered by any currently
  // visible product and auto-select the first one that is (also force-selects the first striped
  // color on .cnav-striped-duplicate cards and hides their own swatch picker).
  document.dispatchEvent(new CustomEvent('cnav:filter-changed', { detail: { handle } }));
};

const initCollectionNavigationFilter = () => {
  const cards = document.querySelectorAll('.cnav-card[data-collection-handle]');
  if (!cards.length) {
    return;
  }

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      const isAlreadyActive = card.classList.contains('is-active');
      const nextHandle = isAlreadyActive ? null : card.getAttribute('data-collection-handle');

      setActiveCard(cards, isAlreadyActive ? null : card);
      applyProductFilter(nextHandle);

      if (window.history) {
        const params = new URLSearchParams(window.location.search);
        if (nextHandle) {
          params.set('collection', nextHandle);
        } else {
          params.delete('collection');
        }
        const query = params.toString();
        history.pushState({}, '', `${window.location.pathname}${query ? '?' + query : ''}`);
      }
    });
  });

  const requestedHandle = new URLSearchParams(window.location.search).get('collection');
  const matchingCard = requestedHandle
    ? Array.from(cards).find((card) => card.getAttribute('data-collection-handle') === requestedHandle)
    : null;

  if (matchingCard) {
    setActiveCard(cards, matchingCard);
  }

  // Always run once on load, even with no requested category — this is what dedupes products
  // shared across combined collections and repositions striped duplicates (see applyProductFilter),
  // not just after a card click.
  applyProductFilter(matchingCard ? requestedHandle : null);
};

initCollectionNavigationReveal();
initCollectionNavigationFilter();
